import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { School } from '@awo-care/shared-core';

export interface SchoolGroup {
  kreis: string;
  schools: School[];
}

/** Kreis ohne Angabe landet in einer eigenen Gruppe am Ende der Liste. */
const FALLBACK_KREIS = 'Sonstige';

/** Klein geschrieben und ohne Akzente, damit „Gießen“ auch auf „giessen“ passt. */
function normalize(text: string): string {
  return text
    .toLocaleLowerCase('de-DE')
    .replace(/ß/g, 'ss')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '');
}

export function isSchool(value: unknown): value is School {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as School).id === 'string' &&
    typeof (value as School).schoolName === 'string'
  );
}

/** Schulname mit Ort — so unterscheiden Eltern gleichnamige Schulen. */
export function schoolDisplayName(school: School | null | undefined): string {
  if (!school) {
    return '';
  }
  const city = school.address?.city?.trim();
  return city ? `${school.schoolName}, ${city}` : school.schoolName;
}

/**
 * Treffer auf Schulname oder Ort. Mehrere Wörter müssen alle vorkommen,
 * damit „erk langen“ die Ludwig-Erk-Schule in Langen findet.
 */
export function filterSchools(schools: School[], query: string): School[] {
  const words = normalize(query).split(/\s+/).filter(Boolean);
  if (words.length === 0) {
    return schools;
  }
  return schools.filter((school) => {
    const haystack = normalize(
      `${school.schoolName} ${school.address?.city ?? ''} ${school.kreis ?? ''}`
    );
    return words.every((word) => haystack.includes(word));
  });
}

/** Nach Kreis gruppiert, Kreise und Schulen jeweils alphabetisch. */
export function groupSchoolsByKreis(schools: School[]): SchoolGroup[] {
  const grouped = new Map<string, School[]>();
  for (const school of schools) {
    const kreis = school.kreis || FALLBACK_KREIS;
    const group = grouped.get(kreis) ?? [];
    group.push(school);
    grouped.set(kreis, group);
  }
  return Array.from(grouped.entries())
    .sort(([a], [b]) => {
      if (a === FALLBACK_KREIS) return 1;
      if (b === FALLBACK_KREIS) return -1;
      return a.localeCompare(b, 'de');
    })
    .map(([kreis, list]) => ({
      kreis,
      schools: [...list].sort((a, b) =>
        a.schoolName.localeCompare(b.schoolName, 'de')
      ),
    }));
}

/**
 * Nur ein aus der Liste gewähltes Schul-Objekt zählt. Freier Text im
 * Suchfeld ist noch keine Auswahl — sonst käme ein Tippfehler als Schule an.
 * Leere Werte prüft `Validators.required`.
 */
export function schoolSelectedValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    if (value === null || value === undefined || value === '') {
      return null;
    }
    return isSchool(value) ? null : { schoolNotSelected: true };
  };
}
